import { useEffect, useRef, useState } from 'react'
import { View, Text, Animated, Easing, StyleSheet, useWindowDimensions, AccessibilityInfo } from 'react-native'
import { LinearGradient } from 'expo-linear-gradient'
import { useAuth } from '../lib/AuthContext'
import { fonts } from '../lib/theme'

// Transizione uomini <-> donne, sulla falsariga di VB (components/ui.jsx → CompetitionTransition): una banda col gradiente del brand
// attraversa lo schermo da sinistra a destra; a banda quasi piena compare il nome della competizione, poi la banda esce e rivela
// il contenuto gia' cambiato. Con "riduci movimento" attivo: solo un velo piatto e breve. Lo stato `flash` arriva da AuthContext.switchCompetition.
function shade(hex, amount) {
  const n = parseInt(hex.replace('#', ''), 16)
  let r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255
  const target = amount < 0 ? 0 : 255
  const p = Math.abs(amount)
  r = Math.round(r + (target - r) * p); g = Math.round(g + (target - g) * p); b = Math.round(b + (target - b) * p)
  return `rgb(${r},${g},${b})`
}
const FALLBACK_COLORS = ['#000', '#000']

export default function CompetitionTransition() {
  const { flash } = useAuth()
  const { width, height } = useWindowDimensions()
  const panelWidth = width * 1.3 // oltre lo schermo: mai un bordo visibile a copertura piena
  const translateX = useRef(new Animated.Value(-panelWidth)).current
  const opacity = useRef(new Animated.Value(0)).current // solo per il fallback riduci-movimento
  const labelOpacity = useRef(new Animated.Value(0)).current
  const labelScale = useRef(new Animated.Value(0.85)).current
  const [colors, setColors] = useState(flash?.colors ?? FALLBACK_COLORS)
  const [label, setLabel] = useState(flash?.label ?? null)
  const [reduceMotion, setReduceMotion] = useState(false)

  useEffect(() => {
    AccessibilityInfo.isReduceMotionEnabled?.().then(setReduceMotion).catch(() => {})
    const sub = AccessibilityInfo.addEventListener?.('reduceMotionChanged', setReduceMotion)
    return () => sub?.remove?.()
  }, [])

  useEffect(() => {
    if (!flash) return
    const [dark, mid, light] = flash.colors
    setColors([shade(dark, -0.4), dark, mid, light, shade(light, 0.4)])
    setLabel(flash.label ?? null)

    if (reduceMotion) {
      translateX.setValue(-panelWidth)
      labelOpacity.setValue(0)
      opacity.setValue(0.35)
      Animated.timing(opacity, { toValue: 0, duration: 260, useNativeDriver: true }).start()
      return
    }

    translateX.setValue(-panelWidth)
    Animated.sequence([
      Animated.timing(translateX, { toValue: 0, duration: 440, easing: Easing.out(Easing.cubic), useNativeDriver: true }),
      Animated.delay(160),
      Animated.timing(translateX, { toValue: width, duration: 400, easing: Easing.in(Easing.cubic), useNativeDriver: true }),
    ]).start()

    // il nome compare quando la banda copre ~75% dello schermo e sparisce prima che il bordo d'uscita arrivi al centro
    labelScale.setValue(0.85)
    labelOpacity.setValue(0)
    Animated.sequence([
      Animated.delay(110),
      Animated.parallel([
        Animated.timing(labelOpacity, { toValue: 1, duration: 240, useNativeDriver: true }),
        Animated.timing(labelScale, { toValue: 1, duration: 240, easing: Easing.out(Easing.back(1.4)), useNativeDriver: true }),
      ]),
      Animated.delay(260),
      Animated.timing(labelOpacity, { toValue: 0, duration: 220, useNativeDriver: true }),
    ]).start()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flash?.id, reduceMotion])

  return (
    <View pointerEvents="none" style={[StyleSheet.absoluteFillObject, { zIndex: 9999, overflow: 'hidden' }]}>
      <Animated.View style={[StyleSheet.absoluteFillObject, { opacity }]}>
        <LinearGradient colors={colors} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }} style={StyleSheet.absoluteFillObject} />
      </Animated.View>
      <Animated.View style={{ position: 'absolute', top: 0, left: 0, width: panelWidth, height, transform: [{ translateX }] }}>
        <LinearGradient colors={colors} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }} style={{ width: panelWidth, height }} />
      </Animated.View>
      {label && (
        <Animated.View style={[StyleSheet.absoluteFillObject, { alignItems: 'center', justifyContent: 'center', opacity: labelOpacity, transform: [{ scale: labelScale }] }]}>
          <Text
            numberOfLines={2}
            style={{
              width: width * 0.8, fontFamily: fonts.condensedBold, fontSize: 42, lineHeight: 46, color: '#FFFFFF',
              textAlign: 'center', textTransform: 'uppercase', letterSpacing: 1.2,
              textShadowColor: 'rgba(0,0,0,0.35)', textShadowOffset: { width: 0, height: 1 }, textShadowRadius: 6,
            }}
          >
            {label}
          </Text>
        </Animated.View>
      )}
    </View>
  )
}
